/**
 * The adherence judge's "followed the coach" metric, shared by the managers' Adherence scoreboard
 * (AI Coaching Review) and the rep's own tile in the focused view. Both must rank and colour a rep
 * the same way, so the share, its tiers and the minimum sample live here rather than in either view.
 */

/** One rep's judged calls over a range, as `aida/coffee/coaching/adherence/scoreboard` and `.../me` return it. */
export interface CoachingAdherenceRepScore {
	userId: string;
	name: string;
	/** Role, quota team and manager as of the range's last day; absent from an API that predates them, and from `.../me`. */
	role?: string | null;
	quotaTeam?: string | null;
	managerUserId?: string | null;
	managerName?: string | null;
	/** The rep's own cohort, so an all-reps board can tell them apart; null for a rep in none. */
	cohort?: string | null;
	/** Whether the live coach suggests on its own for this rep (auto mode) rather than only on "Get suggestion"; their setting now. */
	autoSuggestions?: boolean | null;
	/**
	 * Prospecting dials (cold demo dials included), connects and demos booked over the range, counted as the warehouse's
	 * dials per demo counts them. Absent from an API that predates them, and from `.../me`.
	 */
	prospectingDials?: number;
	/** Every dial over the range, confirmation and reschedule dials included, so a rep with no calls past the hook still shows they dialed. */
	dials?: number;
	connects?: number;
	demos?: number;
	judgedCalls: number;
	pastHookCalls: number;
	followedCalls: number;
	/** Past-hook calls scored from half the followed score up to it (the yellow band); absent from an API that predates it. */
	partlyFollowedCalls?: number;
	hookRejectedCalls: number;
	hookRejectedFollowedCalls: number;
	bookedCalls: number;
	bookedFollowedCalls: number;
	notScoredCalls: number;
	/**
	 * Calls where the coach's hook, opener or meeting ask could be taken or skipped, and of those how many were taken.
	 * Absent from an API that predates them; zero on calls judged before the judge labeled each move's stage.
	 */
	hookCalls?: number;
	hookUsedCalls?: number;
	openerCalls?: number;
	openerUsedCalls?: number;
	askCalls?: number;
	askUsedCalls?: number;
}

/** Below this many calls past the hook a rep's share is too noisy to rank. */
export const MIN_CALLS_FOR_SHARE = 3;

export type Tier = 'good' | 'warn' | 'bad' | 'neutral';

export const followedShare = (rep: CoachingAdherenceRepScore): number | null =>
	rep.pastHookCalls > 0 ? rep.followedCalls / rep.pastHookCalls : null;

/** Calls past the hook that used the coach at least partly: the API's 30-59 band plus the followed (60+) calls. */
export const partlyOrBetterShare = (rep: CoachingAdherenceRepScore): number | null =>
	rep.pastHookCalls > 0 && rep.partlyFollowedCalls != null
		? (rep.followedCalls + rep.partlyFollowedCalls) / rep.pastHookCalls
		: null;

export const shareTier = (share: number | null): Tier => {
	if (share == null) {
		return 'neutral';
	}
	if (share >= 0.5) {
		return 'good';
	}
	return share >= 0.2 ? 'warn' : 'bad';
};

export const formatPercent = (share: number | null): string => (share == null ? 'n/a' : `${Math.round(share * 100)}%`);
